import * as React from "react"
import { Link } from "@tanstack/react-router"
import {
  BadgeCheck,
  Building2,
  ChevronsUpDown,
  LogOut,
  MessageSquare,
  Monitor,
  Moon,
  Sun,
} from "lucide-react"
import { useStore } from "@tanstack/react-store"
import { useMyOrganizations, organizationStore, organizationActions } from "@/stores/organization.store"
import { useUserDisplay } from "@/hooks/use-user-display"
import { useTheme, localSettingsActions, type Theme } from "@/stores/local-settings.store"
import { UserAccountDialog } from "@/components/user/user-account-dialog"
import { useMySupportSessions } from "@/hooks/use-support-session"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

const themeOrder: Theme[] = ["light", "dark", "system"]
const themeIcons: Record<Theme, React.ReactNode> = {
  light: <Sun className="h-4 w-4" />,
  dark: <Moon className="h-4 w-4" />,
  system: <Monitor className="h-4 w-4" />,
}
const themeLabels: Record<Theme, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
}

/**
 * The tenant row pinned to the bottom of the sidebar: the active organization
 * with the signed-in user beneath it. One menu carries both the account
 * actions (formerly the header avatar menu) and, for multi-org users, the
 * organization switcher. In the icon rail it collapses to the org avatar.
 */
export function TenantMenu() {
  const { isMobile } = useSidebar()
  const { organizations, isLoading } = useMyOrganizations()
  const { sessions: supportSessions } = useMySupportSessions()
  const supportOrganizationIds = new Set(supportSessions.map((session) => session.organizationId))
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const { displayName, initials, email, handleLogout } = useUserDisplay()
  const theme = useTheme()
  const [accountDialogOpen, setAccountDialogOpen] = React.useState(false)

  const activeOrganization =
    organizations.find((org) => org.id === selectedOrganizationId) ?? organizations[0]
  const organizationName = isLoading
    ? "Loading..."
    : activeOrganization?.name ?? "No organizations"

  const cycleTheme = () => {
    const nextTheme = themeOrder[(themeOrder.indexOf(theme) + 1) % themeOrder.length]
    localSettingsActions.setTheme(nextTheme)
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg overflow-hidden">
                  {activeOrganization?.logoUrl ? (
                    <img src={activeOrganization.logoUrl} alt="" className="size-8 object-cover" />
                  ) : (
                    <Building2 className="size-4" />
                  )}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{organizationName}</span>
                  <span className="truncate text-xs text-sidebar-foreground/70">{displayName}</span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-56 rounded-lg"
              align="end"
              side={isMobile ? "top" : "right"}
              sideOffset={8}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{displayName}</span>
                    <span className="truncate text-xs">{email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => setAccountDialogOpen(true)}>
                  <BadgeCheck />
                  Account
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/feedback">
                    <MessageSquare />
                    Feedback
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                    cycleTheme()
                  }}
                >
                  {themeIcons[theme]}
                  Theme: {themeLabels[theme]}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              {organizations.length > 1 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>
                    <span className="block text-xs text-muted-foreground">Organizations</span>
                  </DropdownMenuLabel>
                  {organizations.map((org, index) => (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() => organizationActions.setSelectedOrganization(org.id)}
                      className="p-2"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border overflow-hidden">
                        {org.logoUrl ? (
                          <img src={org.logoUrl} alt={org.name} className="size-6 object-cover shrink-0" />
                        ) : (
                          <Building2 className="size-3.5 shrink-0" />
                        )}
                      </div>
                      {org.name}
                      {supportOrganizationIds.has(org.id) && (
                        <Badge variant="warning">
                          Support
                        </Badge>
                      )}
                      <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <UserAccountDialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen} />
    </>
  )
}
